// src/services/adminStatsService.ts
import { 
    collection, 
    query, 
    where, 
    getDocs,
    orderBy,
    limit as firestoreLimit,
    Timestamp
  } from 'firebase/firestore';
  import { db } from '../config/firebase';
  import { FirebaseProduct } from '../types/product';
  
  // Helper functions for date manipulation
  const startOfMonth = (date: Date): Date => {
    const start = new Date(date);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    return start;
  };
  
  const endOfMonth = (date: Date): Date => {
    const end = new Date(date);
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    end.setHours(23, 59, 59, 999);
    return end;
  };
  
  interface Order {
    id: string;
    total: number;
    customerId: string;
    createdAt: Timestamp;
    // Add other order fields as needed
  }
  
  export interface OrderStats {
    totalOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
    monthlyOrders: {
      month: string;
      orders: number;
      revenue: number;
    }[];
  }
  
  export interface ProductStats {
    totalProducts: number;
    lowStockProducts: FirebaseProduct[];
    outOfStockProducts: FirebaseProduct[];
  }
  
  export interface DashboardStats {
    orderStats: OrderStats;
    productStats: ProductStats;
    recentOrders: Order[];
    growthStats: {
      revenue: number;
      orders: number;
      customers: number;
    };
  }
  
  class AdminStatsService {
    private ordersCollection = collection(db, 'orders');
    private productsCollection = collection(db, 'products');
  
    async getDashboardStats(): Promise<DashboardStats> {
      try {
        const [orderStats, productStats, recentOrders] = await Promise.all([
          this.getOrderStats(),
          this.getProductStats(),
          this.getRecentOrders()
        ]);
  
        const growthStats = await this.calculateGrowth();
  
        return {
          orderStats,
          productStats,
          recentOrders,
          growthStats
        };
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        throw error;
      }
    }
  
    private async getOrderStats(): Promise<OrderStats> {
      const orderDocs = await getDocs(this.ordersCollection);
      const orders = orderDocs.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Order[];
  
      // Calculate total revenue and orders
      const totalRevenue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
      const totalOrders = orders.length;
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  
      // Group orders by month
      const monthlyOrders = this.groupOrdersByMonth(orders);
  
      return {
        totalOrders,
        totalRevenue,
        averageOrderValue,
        monthlyOrders
      };
    }
  
    private async getProductStats(): Promise<ProductStats> {
      const productsSnapshot = await getDocs(this.productsCollection);
      const products = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FirebaseProduct[];
  
      return {
        totalProducts: products.length,
        lowStockProducts: products.filter(p => p.stock > 0 && p.stock <= 10),
        outOfStockProducts: products.filter(p => p.stock === 0)
      };
    }
  
    private async getRecentOrders(limitCount: number = 5): Promise<Order[]> {
      const q = query(
        this.ordersCollection,
        orderBy('createdAt', 'desc'),
        firestoreLimit(limitCount)
      );
  
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Order[];
    }
  
    private async calculateGrowth(): Promise<{ revenue: number; orders: number; customers: number }> {
      const currentMonthStart = startOfMonth(new Date());
      const currentMonthEnd = endOfMonth(new Date());
  
      const previousMonthStart = startOfMonth(new Date(currentMonthStart));
      previousMonthStart.setMonth(previousMonthStart.getMonth() - 1);
      const previousMonthEnd = endOfMonth(new Date(previousMonthStart));
  
      const [currentStats, previousStats] = await Promise.all([
        this.getMonthlyStats(currentMonthStart, currentMonthEnd),
        this.getMonthlyStats(previousMonthStart, previousMonthEnd)
      ]);
  
      return {
        revenue: this.calculateGrowthPercentage(currentStats.revenue, previousStats.revenue),
        orders: this.calculateGrowthPercentage(currentStats.orders, previousStats.orders),
        customers: this.calculateGrowthPercentage(currentStats.customers, previousStats.customers)
      };
    }
  
    private async getMonthlyStats(start: Date, end: Date) {
      const ordersQuery = query(
        this.ordersCollection,
        where('createdAt', '>=', Timestamp.fromDate(start)),
        where('createdAt', '<=', Timestamp.fromDate(end))
      );
  
      const orders = await getDocs(ordersQuery);
      const ordersData = orders.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Order[];
  
      const uniqueCustomers = new Set(ordersData.map(order => order.customerId));
  
      return {
        revenue: ordersData.reduce((sum, order) => sum + (order.total || 0), 0),
        orders: ordersData.length,
        customers: uniqueCustomers.size
      };
    }
  
    private calculateGrowthPercentage(current: number, previous: number): number {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    }
  
    private groupOrdersByMonth(orders: Order[]): { month: string; orders: number; revenue: number }[] {
      const grouped = orders.reduce((acc: Record<string, { month: string; orders: number; revenue: number }>, order) => {
        if (!order.createdAt) return acc;
  
        const date = order.createdAt.toDate();
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!acc[monthKey]) {
          acc[monthKey] = {
            month: monthKey,
            orders: 0,
            revenue: 0
          };
        }
        
        acc[monthKey].orders++;
        acc[monthKey].revenue += order.total || 0;
        
        return acc;
      }, {});
  
      return Object.values(grouped).sort((a, b) => a.month.localeCompare(b.month));
    }
  }
  
  export const adminStatsService = new AdminStatsService();